import { useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient, type QueryKey } from 'react-query';
import { useAtomValue } from 'jotai/utils';
import { useAlerts } from 'src/common/AlertManager';
import { effectiveChatActionModeAtom } from 'src/shared/chatPreferences';
import type { UIAction } from './types';

// Window during which identical toasts (same type + message) coming from
// the chat stream are suppressed. Matches the ~one-tool-call-per-second
// cadence of Claude tool loops. See docs/design/chat-batched-ops-ui-handoff.md.
const TOAST_DEDUPE_WINDOW_MS = 1500;

export function useUIActionDispatcher() {
  const history = useHistory();
  const queryClient = useQueryClient();
  const actionMode = useAtomValue(effectiveChatActionModeAtom);
  const { showSuccess, showInfo, showWarning } = useAlerts();

  // Coalesce queryClient.invalidateQueries calls that arrive within the same
  // animation frame. A turn that calls update_metadata 50 times produces 50
  // identical or per-asset invalidations; without this they fire 50 React
  // Query refetches in quick succession.
  const pendingInvalidations = useRef<Map<string, QueryKey>>(new Map());
  const invalidationFrame = useRef<number | null>(null);

  const flushInvalidations = useCallback(() => {
    invalidationFrame.current = null;
    const keys = Array.from(pendingInvalidations.current.values());
    pendingInvalidations.current.clear();
    keys.forEach((key) => queryClient.invalidateQueries(key));
  }, [queryClient]);

  const scheduleInvalidate = useCallback(
    (key: QueryKey) => {
      pendingInvalidations.current.set(JSON.stringify(key), key);
      if (invalidationFrame.current === null) {
        invalidationFrame.current = requestAnimationFrame(flushInvalidations);
      }
    },
    [flushInvalidations],
  );

  // Track recently-shown toast keys so duplicates within the dedupe window
  // are dropped. The user already sees the previous toast.
  const recentToasts = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    return () => {
      if (invalidationFrame.current !== null) {
        cancelAnimationFrame(invalidationFrame.current);
        invalidationFrame.current = null;
      }
      // Flush any pending work synchronously on unmount so cache state
      // doesn't lag behind. Toast dedupe state is per-hook lifetime — no
      // cleanup needed; the Map is GC'd with the hook.
      pendingInvalidations.current.forEach((key) => queryClient.invalidateQueries(key));
      pendingInvalidations.current.clear();
    };
  }, [queryClient]);

  return useCallback(
    (action: UIAction) => {
      if (action.queryKeys) {
        action.queryKeys.forEach((key) => scheduleInvalidate(key));
      }

      const forceNavigate =
        action.type === 'navigate' && action.route?.startsWith('/ai/schedules/authorize');
      if (action.type === 'navigate' && action.route && (forceNavigate || actionMode === 'navigate')) {
        history.push(action.route);
      }

      if (action.toast) {
        const dedupeKey = `${action.toast.type}:${action.toast.message}`;
        const now = Date.now();
        const last = recentToasts.current.get(dedupeKey);
        if (last !== undefined && now - last < TOAST_DEDUPE_WINDOW_MS) {
          // Drop duplicate within window. We refresh the timestamp so a
          // sustained loop keeps the floodgate closed rather than letting
          // every other toast through at the boundary.
          recentToasts.current.set(dedupeKey, now);
          return;
        }
        recentToasts.current.set(dedupeKey, now);

        const toastPayload: { linkUrl?: string; linkText?: string } = {};
        const link = action.toast.link;
        if (link) {
          toastPayload.linkUrl = link;
          toastPayload.linkText = 'View';
        }

        switch (action.toast.type) {
          case 'success':
            showSuccess(action.toast.message, toastPayload);
            break;
          case 'info':
            showInfo(action.toast.message, toastPayload);
            break;
          case 'warning':
            showWarning(action.toast.message, toastPayload);
            break;
        }
      }
    },
    [history, actionMode, scheduleInvalidate, showSuccess, showInfo, showWarning],
  );
}
